import { useState } from "react"

import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQuery } from "@tanstack/react-query"
import { type PaginationState } from "@tanstack/react-table"
import { ArrowLeft, Save } from "lucide-react"
import { DateTime } from "luxon"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router"
import { toast } from "sonner"
import z from "zod"

import { queryClient } from "@/App.tsx"
import { Button } from "@/components/ui/button.tsx"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx"
import { Input } from "@/components/ui/input.tsx"

import { addReport } from "../../api/report/addReport.ts"
import { ReportRenderer } from "../../api/report/ReportRenderer.ts"
import { updateReport } from "../../api/report/updateReport.ts"
import { type DataPointReport } from "../../types/reportTypes.ts"
import { getRowCount } from "../../util/getRowCount.ts"

import ConstantRangeSelector from "./ConstantRangeSelector.tsx"
import DataPointReportTable from "./DataPointReportTable.tsx"

const constantRange = z.object({
  start: z.date(),
  end: z.date(),
  $type: z.literal("constant"),
})

const fromNowRange = z.object({
  returnRange: z.int().min(1),
  $type: z.literal("fromNow"),
})

const periodicRange = z.object({
  period: z.enum(["minute", "hour", "day", "week", "month", "year"]),
  timezone: z.string(),
  current: z.boolean(),
  $type: z.literal("periodic"),
})

const formSchema = z.object({
  name: z.string().min(1).max(255),
  interval: z.int().min(1),
  dataPoints: z.array(z.int()).min(1),
  aggModes: z.array(
    z.enum([
      "Average",
      "SimpleAverage",
      "LastValue",
      "Sum",
      "Minimum",
      "Maximum",
      "Count",
      "Range",
      "Variance",
      "StandardDeviation",
    ]),
  ),
  range: z.discriminatedUnion("$type", [constantRange, fromNowRange, periodicRange]),
})

const formDefaults: DataPointReportBuilderSchema = {
  name: "",
  interval: 0,
  aggModes: [],
  dataPoints: [],
  range: {
    $type: "constant",
    start: DateTime.now().startOf("day").minus({ days: 1 }).toJSDate(),
    end: DateTime.now().startOf("day").toJSDate(),
  },
}

export type DataPointReportBuilderSchema = z.infer<typeof formSchema>

const reportRenderer = new ReportRenderer()

export default function DataPointReportBuilder({ report }: { report?: DataPointReport }) {
  const navigate = useNavigate()
  const form = useForm<DataPointReportBuilderSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: report ? report : formDefaults,
  })
  const [pagination, setPagination] = useState<PaginationState>({
    pageSize: 16,
    pageIndex: 0,
  })
  const [request, setRequest] = useState<DataPointReport | undefined>()
  const rowCount = request ? getRowCount(request) : 0

  const { data, isFetching } = useQuery({
    queryKey: ["dataPointReportPreview", { request, page: pagination.pageIndex }],
    queryFn: () => reportRenderer.getReport(request, pagination.pageIndex, pagination.pageSize),
  })

  const addMutation = useMutation({
    mutationFn: (variables: DataPointReport) => addReport(variables),
    onSuccess: () => {
      toast("Report added added", { richColors: true })
      queryClient.invalidateQueries({ queryKey: ["allDataPointReports"] })
      navigate("/datapoints/reports")
    },
  })

  const updateMutation = useMutation({
    mutationFn: (variables: DataPointReport) => updateReport(report?.id || 0, variables),
    onSuccess: () => {
      toast("Report updated", { richColors: true })
      queryClient.invalidateQueries({ queryKey: ["allDataPointReports"] })
      queryClient.invalidateQueries({
        queryKey: ["dataPointReport", { id: report?.id }],
      })
      navigate("/datapoints/reports")
    },
  })

  const handlePreview = () => {
    const formData = form.watch()
    setRequest({
      name: formData.name,
      interval: formData.interval,
      range: formData.range,
      dataPoints: formData.dataPoints,
      aggModes: formData.aggModes,
    })
  }

  const handleSubmit = (data: DataPointReportBuilderSchema) => {
    const req: DataPointReport = {
      name: data.name,
      interval: data.interval,
      range: data.range,
      dataPoints: data.dataPoints,
      aggModes: data.aggModes,
    }

    if (report) {
      updateMutation.mutate(req)
    } else {
      addMutation.mutate(req)
    }
  }

  return (
    <Form {...form}>
      <form className="flex flex-col gap-2 min-w-0" onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="rounded-md shadow-sm border p-4 grid grid-cols-4 gap-2 gap-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="col-span-2 justify-baseline">
                <FormLabel>Report Name</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Enter report name"
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>Descriptive title for report</FormDescription>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="interval"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>Interval</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter report interval"
                    type="number"
                    value={field.value}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
                <FormDescription>Number of seconds each row spans</FormDescription>
              </FormItem>
            )}
          />
          {form.watch().range.$type == "constant" && <ConstantRangeSelector form={form} />}
          <div className="flex justify-end col-span-4 gap-2">
            <Button variant="outline" type="button" onClick={() => navigate("/datapoints/reports")}>
              <ArrowLeft />
              Cancel
            </Button>
            <Button
              variant="default"
              type="button"
              onClick={handlePreview}
              disabled={!form.formState.isValid}
            >
              Load Preview
            </Button>
            <Button type="submit" variant="default" disabled={!form.formState.isValid}>
              <Save /> Save Report
            </Button>
          </div>
        </div>

        <DataPointReportTable
          pagination={pagination}
          setPagination={setPagination}
          form={form}
          data={data || []}
          isLoading={isFetching}
          rowCount={rowCount}
        />
      </form>
    </Form>
  )
}
